import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useSearchParams } from 'react-router-dom';
import { EmailsModule } from './emails/EmailsModule';
import { getBrokersAsync } from '@application/features/brokers/brokerThunk';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Box, Button, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { BrokerDialog } from '../clients/add-new-client/dialog/BrokerDialog';
import { BrokerModule, useBrokerCount } from './broker';
import { HourTypesModule, InternalDepartmentsModule, ProjectTaskMappingsModule } from './hourRouting';
import { BusinessCentralStagingModule } from './hourRouting/BusinessCentralStagingModule';
import { BusinessDecisionLogModule } from './businessDecisionLog/BusinessDecisionLogModule';
import { SettingsBar, SettingsSidebar } from './SettingsBar';
import { PortalCapabilitiesApi, unavailablePortalCapabilities } from '@infrastructure/portal-capabilities-api';
import { MySecurity } from './access/MySecurity';
import { TeamAccess } from './access/TeamAccess';
import { ConfirmProvider, StepUpProvider } from './access/SecurityParts';
import { McpConnections } from './access/McpConnections';
import { ConnectedApps } from '../../connect/ConnectedApps';
const routingNavigation = [
    { id: 'hour-types', label: 'Hour types', description: 'Names and immutable routing policy' },
    { id: 'internal-departments', label: 'Internal Departments', description: 'Internal BC projects and readiness' },
    { id: 'project-task-mappings', label: 'Project task numbers', description: 'Date-effective internal mappings' }
];
export const Settings = () => {
    const dispatch = useAppDispatch();
    const role = useAppSelector((state) => state.auth.currentUser?.role);
    const isSuperAdmin = role === 'SuperAdmin';
    const canReadStaging = isSuperAdmin || role === 'Admin';
    // Admin security (add-rebels-admin-security-and-mcp-connectors, R4.3): own security for every team member.
    const isTeamMember = isSuperAdmin || role === 'Admin';
    const [capabilities, setCapabilities] = useState(unavailablePortalCapabilities);
    const canUseEmailManagement = canReadStaging && capabilities.emailManagement && capabilities.emailCockpit;
    const brokerCount = useBrokerCount();
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab');
    const defaultTab = isSuperAdmin ? 'hour-types' : 'broker';
    const availableTabs = ['broker', ...(canReadStaging ? ['business-central'] : []),
        ...(canUseEmailManagement ? ['emails'] : []),
        ...(isSuperAdmin ? [...routingNavigation.map(item => item.id), 'logbook', 'team'] : []),
        ...(isTeamMember ? ['security'] : [])];
    const activeTab = availableTabs.includes(requestedTab) ? requestedTab : defaultTab;
    const setActiveTab = (tab) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', tab);
        setSearchParams(next);
    };
    const [openBrokerAdd, setOpenBrokerAdd] = useState(false);
    const brokerNavigation = {
        id: 'broker',
        label: `Broker config (${brokerCount})`,
        description: 'Existing broker administration'
    };
    useEffect(() => {
        dispatch(getBrokersAsync());
    }, [dispatch]);
    useEffect(() => {
        if (!canReadStaging) {
            setCapabilities(unavailablePortalCapabilities);
            return;
        }
        const controller = new AbortController();
        PortalCapabilitiesApi.get(controller.signal).then(setCapabilities).catch(() => setCapabilities(unavailablePortalCapabilities));
        return () => controller.abort();
    }, [canReadStaging]);
    return (_jsxs(Box, { className: "settings-workspace", children: [_jsx(SettingsBar, {}), _jsxs(Box, { className: "settings-workspace__body", children: [_jsx(SettingsSidebar, { value: activeTab, onChange: setActiveTab, items: [
                            ...(isSuperAdmin ? routingNavigation.map(item => ({ value: item.id, label: item.label })) : []),
                            ...(canReadStaging ? [
                                { value: 'business-central', label: 'Staging records' }
                            ] : []),
                            ...(canUseEmailManagement ? [
                                { value: 'emails', label: 'Emails' }
                            ] : []),
                            ...(isSuperAdmin ? [
                                { value: 'logbook', label: 'Logboek' }
                            ] : []),
                            { value: brokerNavigation.id, label: 'Broker config', count: brokerCount },
                            ...(isSuperAdmin ? [
                                { value: 'team', label: 'Team & access' }
                            ] : []),
                            ...(isTeamMember ? [
                                { value: 'security', label: 'My security' }
                            ] : [])
                        ] }), _jsxs(Box, { className: "settings-workspace__content", children: [isSuperAdmin && activeTab === 'hour-types' && _jsx(HourTypesModule, {}), isSuperAdmin && activeTab === 'internal-departments' && _jsx(InternalDepartmentsModule, {}), isSuperAdmin && activeTab === 'project-task-mappings' && _jsx(ProjectTaskMappingsModule, {}), canReadStaging && activeTab === 'business-central' && _jsx(BusinessCentralStagingModule, {}), canUseEmailManagement && activeTab === 'emails' && _jsx(EmailsModule, {}), isSuperAdmin && activeTab === 'logbook' && _jsx(BusinessDecisionLogModule, { mcp: {
                                    view: searchParams.get('log') === 'mcp' ? 'mcp' : 'decisions',
                                    userId: searchParams.get('mcpUser') || '',
                                    clientId: searchParams.get('mcpClient') || '',
                                    onView: (view) => {
                                        const next = new URLSearchParams(searchParams);
                                        next.set('log', view);
                                        if (view !== 'mcp') {
                                            next.delete('mcpUser');
                                            next.delete('mcpClient');
                                        }
                                        setSearchParams(next);
                                    }
                                } }), _jsx(ConfirmProvider, { children: _jsxs(StepUpProvider, { children: [isSuperAdmin && activeTab === 'team' && _jsxs(_Fragment, { children: [_jsx(TeamAccess, {}), _jsx(McpConnections, { onShowActivity: (c) => {
                                                        // Logboek → MCP-activiteit for this person and app (add-admin-mail-actions-and-mcp-audit, R-E2).
                                                        const next = new URLSearchParams({ tab: 'logbook', log: 'mcp' });
                                                        if (c.userId)
                                                            next.set('mcpUser', c.userId);
                                                        if (c.clientId)
                                                            next.set('mcpClient', c.clientId);
                                                        setSearchParams(next);
                                                    } })] }), isTeamMember && activeTab === 'security' && (_jsxs(_Fragment, { children: [_jsx(MySecurity, {}), _jsx(ConnectedApps, { headingComponent: 'h3', sx: { bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, p: { xs: 2, md: 2.5 }, mt: 2, mb: 2 } })] }))] }) }), activeTab === 'broker' && (_jsxs(Box, { component: 'section', sx: {
                                    bgcolor: 'background.paper',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }, children: [_jsxs(Stack, { direction: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, spacing: 2, sx: { px: { xs: 2, md: 3 }, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: 'h5', fontWeight: 600, children: "Broker config" }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', sx: { mt: 0.5 }, children: "Existing broker administration is unchanged by hour routing." })] }), _jsx(Button, { "data-action-motion": 'true', variant: 'contained', onClick: () => setOpenBrokerAdd(true), children: "Add broker" })] }), _jsx(Box, { sx: { p: { xs: 2, md: 3 } }, children: _jsx(BrokerModule, {}) })] }))] })] }), _jsx(BrokerDialog, { handleClose: () => setOpenBrokerAdd(false), open: openBrokerAdd, type: 'add', onBrokerSaved: () => {
                    dispatch(getBrokersAsync());
                    setOpenBrokerAdd(false);
                } })] }));
};
